import { Card, CardContent, CardMedia, Skeleton, Stack, Typography } from '@mui/material';
import { memo, ReactNode, useState, useEffect } from 'react';
import { SK_LOGO } from 'constants/images';

interface IGuideCardProps {
  isLoading: boolean;
  name: string;
  desc: string;
  content: ReactNode;
  content2?: ReactNode;
  customStyle?: {
    xs: number;
    sm: number;
    maxHeight: number;
  };
}
function GuideCard({ isLoading, name, desc, content, content2, customStyle }: IGuideCardProps): JSX.Element {
  // 동적 높이 계산
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 버튼 높이 + 헤더 여백 고려 (모바일: 72px + 80px, 데스크탑: 84px + 100px)
  const buttonAreaHeight = window.innerWidth < 600 ? 72 : 84;
  const headerPadding = window.innerWidth < 600 ? 80 : 100;
  const availableHeight = windowHeight - buttonAreaHeight - headerPadding;

  return (
    <Card
      sx={{
        minWidth: 240,
        backgroundColor: '#fff',
        maxWidth: 1280,
        boxShadow: 'none',
        width: 'auto',
      }}
    >
      <CardContent sx={{ pb: { xs: 1, sm: 2 } }}>
        {isLoading ? (
          <Skeleton variant="rectangular" width={120} height={60} sx={{ borderRadius: '8px', mb: 2 }} />
        ) : (
          <CardMedia component="img" image={SK_LOGO} alt="SK hynix logo" sx={{ width: 120, height: 'auto', mb: 2 }} />
        )}
        <Typography sx={{ typography: 's16w800' }} color="mind.grey.brown">
          {isLoading ? <Skeleton variant="text" width={200} /> : name}
        </Typography>
        <Typography sx={{ mt: '5px', mb: '10px', typography: 's14w400' }} color="mind.grey.brown">
          {isLoading ? <Skeleton variant="text" width={300} /> : desc}
        </Typography>
        <CardMedia
          component="div"
          sx={{
            width: { xs: '100%', sm: '100%' },
            minHeight: { xs: customStyle?.xs ?? 300, sm: customStyle?.sm ?? 400 },
            maxHeight: `${availableHeight}px`,
            objectFit: 'fill',
            position: 'relative',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Stack
            direction="column"
            spacing="20px"
            sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: { xs: 1, sm: 2 } }}
          >
            {content || (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  minHeight: { xs: customStyle?.xs ?? 300, sm: customStyle?.sm ?? 400 },
                }}
              />
            )}
            {content2 && content2}
          </Stack>
        </CardMedia>
      </CardContent>
    </Card>
  );
}

export default memo(GuideCard);
